import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// utils
import { TextContext } from '@/utils/contexts/TextContext';

// components
import TextEditor from '@/views/elements/TextEditor';
import SingleChange from '@/views/pages/Letter/elements/LetterPopover/SingleChange';
import Spinner from '@/views/components/Spinner/Spinner';
import TooltipIcon from "@/views/components/TooltipIcon/TooltipIcon";

import styles from '@/views/pages/Letter/elements/LetterPopover/LetterPopover.module.scss';

const LetterPopover = ({ letterWindowWidth, popoverWidth }) => {
	const { dict } = useSelector((state) => state.app);

	const {
		contextState, contextDispatch,
	} = useContext(TextContext);

	const { user } = useSelector(state => state.auth);

	const [ originalSpansContent, setOriginalSpansContent ] = useState(null);

	const [ content, setContent ] = useState('');
	const [ annotation, setAnnotation ] = useState('');
	const [ reason, setReason ] = useState('');
	const [ currentChangesHistoryId, setCurrentChangesHistoryId ] = useState(null);

	const highlightChangeTextById = (changeId) => {
		const originalChange = {};

		const selectedChange = contextState.currentChangesHistory.changes.filter(it => it.id === changeId)[0];
		if (!selectedChange) return;
		Object.keys(selectedChange.highlighting).forEach(it => {
			if (!document.getElementById(it)) return;
			originalChange[it] = document.getElementById(it).innerHTML;
			document.getElementById(it).innerHTML = selectedChange.highlighting[it];
		});
		if (!originalSpansContent) {
			setOriginalSpansContent(originalChange);
		}
	};

	useEffect(() => {
		// in SingleChange round icon clicked -> highlight appropriate part of the text 
		if (!contextState.currentChangesHistory) return;
		highlightChangeTextById(contextState.currentSelectedChangeId);
	}, [ contextState.currentSelectedChangeId ]);

	useEffect(() => {
		if (!contextState.currentChangesHistory) return;
		if (currentChangesHistoryId === contextState.currentChangesHistory.changeId) {
			// data of CURRENT opened change has been changed
			//if it was edited - just highlight text
			// if it was deleted - find first change of currentChangesHistory and highlight it
			if (contextState.currentSelectedChangeId) {
				if (contextState.currentChangesHistory.changes.some(el => el.id === contextState.currentSelectedChangeId)) {
					// there is currentSelectedChangeId in currentChangeHistory so just highlight text
					// restoreOriginalSpansContent();
					highlightChangeTextById(contextState.currentSelectedChangeId);
				} else {
					// no currentSelectedChangeId in currentChangeHistory - probably it has been removed
					contextDispatch({
						type: 'setCurrentSelectedChangeId', payload: {
							changeId: contextState.currentChangesHistory.changes[0].id,
						},
					});
				}
			}
		} else {
			// check is it a new change?

			restoreOriginalSpansContent();
			// becoming new change - probably handler was clicked
			setCurrentChangesHistoryId(parseInt(contextState.currentChangesHistory.changeId));
			contextDispatch({
				type: 'setCurrentSelectedChangeId', payload: {
					changeId: contextState.currentChangesHistory.changes[0].id,
				},
			});
		}
	}, [ contextState.currentChangesHistory ]);

	const onAddChange = () => {
		// add total new change
		if (contextState.popoverMode === 0) {
			contextDispatch({
				type: 'setPopoverMode', payload: {
					popoverMode: 1,
				},
			});
			contextDispatch({ type: 'highlightText' });
		} else if (contextState.popoverMode === 1) {
			contextDispatch({
				type: 'handleChangeConfirmed', payload: {
					change: {
						content, annotation, reason,
					},
					newChange: true,
				},
			});
			clearChangeFormState();
		}
	};

	const onCancelChange = () => {
		contextDispatch({
			type: 'setPopoverMode', payload: {
				popoverMode: 0,
			},
		});
		contextDispatch({ type: 'clearHighlightText' });
	};

	const onClosePopover = () => {
		contextDispatch({
			type: 'setCurrentSelectedChangeId', payload: {
				changeId: 0,
			},
		});
		restoreOriginalSpansContent();

		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		contextDispatch({ type: 'resetChangesHistory' });
	};

	/// ADDING new change to existing changes
	const getIntoAddChangeToExistigMode = () => {
		// get into proper mode
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: true,
			},
		});
	};

	const moveTextToFooter = () => {
		contextDispatch({
			type: 'handleChangeConfirmed', payload: {
				change: {
					content: "", 
					annotation: contextState.caret.text, 
					reason: null,
					start: contextState.caret.start,
					end: contextState.caret.end,
				},
				newChange: true,
			},
		});
		// reset addToExisting mode to hide editor and restore ability to edit other changes
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};

	const moveExistingTextToFooter = () => {
		contextDispatch({
			type: 'handleChangeConfirmed', payload: {
				change: {
					content: "", 
					annotation: contextState.currentChangesHistory.changes[0].text,
					reason: null,
					start: contextState.currentChangesHistory.start,
					end: contextState.currentChangesHistory.end,
				},
				newChange: false,
			},
		});
		// reset addToExisting mode to hide editor and restore ability to edit other changes
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};

	const removePartOfText = () => {
		contextDispatch({
			type: 'handleChangeConfirmed', payload: {
				change: {
					content: "", 
					annotation: "", 
					reason: "Usunięte",
					start: contextState.caret.start,
					end: contextState.caret.end,
				},
				newChange: true,

			},
		});
		// reset addToExisting mode to hide editor and restore ability to edit other changes
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};

	const removeExistingPartOfText = () => {
		contextDispatch({
			type: 'handleChangeConfirmed', payload: {
				change: {
					content: "", 
					annotation: "", 
					reason: "Usunięte",
					start: contextState.currentChangesHistory.start,
					end: contextState.currentChangesHistory.end,
				},
				newChange: true,

			},
		});
		// reset addToExisting mode to hide editor and restore ability to edit other changes
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};

	const onConfirmAddExisting = () => {
		// change added to existing history change 
		contextDispatch({
			type: 'handleChangeConfirmed', payload: {
				change: {
					content, annotation, reason,
					start: contextState.currentChangesHistory.start,
					end: contextState.currentChangesHistory.end,
				},
				newChange: false,

			},
		});
		// reset addToExisting mode to hide editor and restore ability to edit other changes
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};
	const onCancelAddExisting = () => {
		contextDispatch({
			type: 'setAddToExistingMode', payload: {
				addToExistingMode: false,
			},
		});
		clearChangeFormState();
	};
	const clearChangeFormState = () => {
		setContent('');
		setAnnotation('');
		setReason('');
	};

	useEffect(() => {
		if (contextState.eventObject) {
			switch (contextState.eventObject.type) {
				case 'changeAdded':
					if (contextState.popoverMode === 1) {
						contextDispatch({
							type: 'setPopoverMode', payload: {
								popoverMode: 0,
							},
						});
					} else if (contextState.popoverMode === 2) {
						onCancelAddExisting();
						contextDispatch({
							type: 'setPopoverMode', payload: {
								popoverMode: 2,
							},
						});
					}
					break;
				case 'changeRemoved':
					contextState.popoverMode === 2 && contextDispatch({
						type: 'setPopoverMode', payload: {
							popoverMode: 0,
						},
					});
					break;
				default:
					contextDispatch({
						type: 'setPopoverMode', payload: {
							popoverMode: 2,
						},
					});
					break;
			}
			contextDispatch({ type: 'resetEventObject' });
		}
		// eslint-disable-next-line
    }, [contextState.eventObject]);

	const restoreOriginalSpansContent = () => {
		if (originalSpansContent) {
			Object.keys(originalSpansContent).forEach(it => {
				if (!document.getElementById(it)) return;
				document.getElementById(it).innerHTML = originalSpansContent[it];
			});
			setOriginalSpansContent(null);
		}
	};

	// DRAGGING POPOVER:
	const [ isDragging, setIsDragging ] = useState(false);

	let draggedFunc;
	const onDragBarMouseDown = (ev) => {
		setIsDragging(true);
		const startDragMouse = { x: ev.nativeEvent.screenX, y: ev.nativeEvent.screenY };
		const startDragPopoverPosition = { ...contextState.popoverPosition };
		draggedFunc = (ev2) => { 
			dragging(
				ev2, 
				startDragMouse, 
				startDragPopoverPosition,
			);
		};
		document.addEventListener('mousemove', draggedFunc);
		document.addEventListener('mouseup', stopDragging, { once: true });
	};

	const dragging = (ev, localStartDragMouse, localStartDragPopover) => {
		contextDispatch({
			type: 'setPopoverPosition', popoverPosition: {
				x: Math.min(Math.max(localStartDragPopover.x + (ev.screenX - localStartDragMouse.x), 0), (letterWindowWidth - (popoverWidth / 2))),
				y: Math.max(localStartDragPopover.y + (ev.screenY - localStartDragMouse.y), 0),
			},
		});
	};

	const stopDragging = () => {
		setIsDragging(false);
		document.removeEventListener('mousemove', draggedFunc);
		document.removeEventListener('mouseup', stopDragging, { once: true });
	};

	useEffect(() => () => {
		stopDragging();
	}, []);

	return (
		<div
			className={ `${styles['letter-popover']}${isDragging ? ' noselect' : ''}` }
			style={ {
				left: contextState.popoverPosition.x + 'px',
				top: contextState.popoverPosition.y + 'px',
				pointerEvents: (contextState.popoverDisablePointerEvents ? 'none' : 'auto'),
				display: (contextState.popoverMode === 0 && (!contextState.caret || contextState.caret.text.length === 0 || user.role === 'reader' || contextState.isMobile || contextState.letterData.version === 'final')) ? 'none' : 'block',
			} }
		>
			{ user.role !== "reader" && (
				<>
					<div
						className={ styles['letter-popover--drag-bar'] }
						onMouseDown={ (ev) => onDragBarMouseDown(ev) }
					>
						<div
							className={ styles['letter-popover--title'] }
						>
							{ contextState.popoverMode === 0 && "Wprowadź zmianę" }
							{ contextState.popoverMode === 1 && "Zmień treść" }
							{ contextState.popoverMode === 2 && (
								contextState.addToExistingMode ? "Zmień treść" : "Wprowadź lub zarządzaj zmianami"
							) }
						</div>
						<div
							className={ styles['letter-popover--close'] }
							onClick={ onClosePopover }
						>x
						</div>
					</div>
				</>
			) }
			{contextState.historyIsLoading && (
				<div className={ styles['letter-popover__spinner--container'] }>
					<Spinner />
				</div>
			)}

			{contextState.popoverMode === 0 && user && user.role !== "reader" && !contextState.isMobile && (
			// description + addChangeButton
				<div className={ `${ styles['letter-popover--container'] } ${ styles['auto-height'] }` }>
					<p className={ `${ styles['letter-popover--comment'] } noselect` }>{dict.letter.popover.comment}</p>
					<div className={ styles['letter-popover--buttons'] }>
						<button
							className={ `${ styles['letter-popover--btn'] } noselect` }
							onClick={ onAddChange }
						>{dict.letter.popover.changeText}
						</button>
						<button
							className={ `${ styles['letter-popover--btn'] } noselect` }
							onClick={ moveTextToFooter }
						>{dict.letter.popover.moveLegalBasis}
						</button>
						<button
							className={ `${ styles['letter-popover--btn'] } noselect` }
							onClick={ removePartOfText }
						>{dict.letter.popover.deleteText}
						</button>
					</div>
				</div>
			)}

			{contextState.popoverMode === 1 && (
			// add change
				<div className={ styles['letter-popover--container'] }>
					<div className={ `${ styles['letter-popover__changes--header'] } ${ styles['top'] }` }>
						<strong>{dict.letter.popover.addChange}</strong>
					</div>
					<TextEditor
						placeholder={ dict.letter.editor.changeContent }
						setFieldContent={ setContent }
						minHeight={ 100 }
						setAutoFocus
					/>
					<div className={ `${ styles['letter-popover__changes--header'] } ${ styles['top'] }` }>
						{dict.letter.popover.reason} <span className={ styles['letter-popover__changes--optional'] }>{dict.letter.popover.reasonOptional}</span>
					</div>
					<TextEditor
						placeholder={ dict.letter.editor.reason }
						setFieldContent={ setReason }
						disableFormating
					/>
					<div className={ styles['letter-popover--buttons'] }>
						<div
							className='button__std'
							onClick={ onCancelChange }
						>{dict.cancel}
						</div>

						<div
							className={ `${ styles['letter-popover--btn'] } noselect${(content.length || annotation.length || reason.length) ? '' : ' disabled'}` }
							onClick={ onAddChange }
						>
							{dict.letter.popover.addChange}
						</div>
					</div>
				</div>
			)}
			{(contextState.popoverMode === 2) && (contextState.currentChangesHistory) && (contextState.currentChangesHistory.changes.length) && (
			// show history of changes
			// you can add new change in this mode by setting addToExistingMode
			// Then there is two methods - onCancelAddExisting and onConfirmAddExisting to upload change to api
				<>
					{!contextState.addToExistingMode ?
						(
							(user.role !== "reader" && !contextState.isMobile) && (
								<div className={ `${ styles['letter-popover--container'] } ${ styles['auto-height'] }` }>
									<p className={ `${ styles['letter-popover--comment'] } noselect` }>{dict.letter.popover.comment}</p>
									<div className={ styles[`letter-popover--buttons`] }>
										<button
											className={ `${ styles['letter-popover--btn'] } noselect` }
											onClick={ getIntoAddChangeToExistigMode }
										>{dict.letter.popover.changeText}
										</button>
										<button
											className={ `${ styles['letter-popover--btn'] } noselect` }
											onClick={ moveExistingTextToFooter }
										>{dict.letter.popover.moveLegalBasis}
										</button>
										<button
											className={ `${ styles['letter-popover--btn'] } noselect` }
											onClick={ removeExistingPartOfText }
										>{dict.letter.popover.deleteText}
										</button>
									</div>
								</div>
							)
						) : (
							<div className={ styles[`letter-popover--container`] }>
								<div className={ `${ styles['letter-popover__changes--header'] } ${ styles['top'] }` }>
									<strong>{dict.letter.popover.addNewChange}</strong>
								</div>
								<TextEditor
									placeholder={ dict.letter.editor.changeContent }
									setFieldContent={ setContent }
									minHeight={ 100 }
									setAutoFocus
								/>
								<div className={ `${ styles['letter-popover__changes--header'] } ${ styles['top'] }` }>
									{dict.letter.popover.reason} <span className={ styles['letter-popover__changes--optional'] }>{dict.letter.popover.reasonOptional}</span>
								</div>
								<TextEditor
									placeholder={ dict.letter.editor.reason }
									setFieldContent={ setReason }
									disableFormating
								/>
								<div className={ styles[`letter-popover--buttons`] }>
									<div
										className='button__std'
										onClick={ onCancelAddExisting }
									>{dict.cancel}
									</div>
									<div
										className={ `${ styles['letter-popover--btn'] } noselect${(content.length || annotation.length || reason.length) ? '' : ' disabled'}` }
										onClick={ onConfirmAddExisting }
									>
										{dict.letter.popover.simplify}
									</div>
								</div>
							</div>
						)}

					<div className={ styles['letter-popover__changes--header'] }>
						<div><strong>{dict.letter.popover.changes}</strong> ({contextState.currentChangesHistory.changes.length})</div>
						<div
							className={ styles['letter-popover__changes--tooltip'] }
						>
							<TooltipIcon
								color="#0066D3"
								tooltipText={ dict.letter.popover.changesTooltip }
							/>
						</div>
						{ user.role !== "reader" ? (
							<>
								<div
									className={ styles['letter-popover__changes--remove-not-selected-automatic-changes'] }
								>
									<button 
										className={ `${ styles['letter-popover-action-btn'] }${(
											contextState.currentChangesHistory && 
											contextState.currentChangesHistory?.changes.length > 1 &&
											contextState.currentChangesHistory?.changes[0].rights.canSelectChangeAndDeleteAuto && 
											contextState.currentChangesHistory?.changes.some((value) => { return value.auto; })
										) ? '': ' disabled'}` }
										onClick={ () => {
											contextDispatch({
												type: 'popoverMenuSelectChangeAndDeleteRemainingAutomatic',
												payload: {
													changeId: contextState.currentSelectedChangeId,
												},
											});
										} }
									>
										<i className="icon-delete" />
										{ dict.letter.popover.deleteNotChosenChanges }
									</button>
								</div>
							</>
						) : (
							<div
								className={ styles['letter-popover--history-close'] }
								onClick={ onClosePopover }
							>x
							</div>
						) }
					</div>

					<div className={ `${ styles['letter-popover__changes--container'] }${contextState.addToExistingMode ? ' disabled' : ''}` }>
						{contextState.currentChangesHistory.changes.map((it) => (
							<div 
								key={ it.id } 
								className={ styles['letter-popover__changes--item-container'] }
							>
								<div>
									<input 
										type="radio"
										style={ user.role === "reader" ? {} : { cursor: "pointer" } }
										disabled={ user.role === "reader" || !it.rights.canSelectChange }
										checked={ contextState.currentSelectedChangeId === it.id }
										onClick={ () => {
											contextDispatch({
												type: 'popoverMenuSelectChange',
												payload: {
													changeId: it.id,
												},
											});
										} }
									/>
								</div>
								<SingleChange
									change={ it }
									highlightChangeFunction={ highlightChangeTextById }
								/>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
};

LetterPopover.propTypes = {
	letterWindowWidth: PropTypes.number,
	popoverWidth: PropTypes.number,
};

export default LetterPopover;